import api from "./api";
import { logAuthDiagnostic } from "./blankPageDiagnostics";
import {
  broadcastAuthSync,
  subscribeAuthSync,
  AUTH_SYNC_EVENTS,
  ACTIVITY_STORAGE_KEY,
} from "./authSyncChannel";

import logger from "./logger";

export const REFRESH_BUFFER_SECONDS = 120; // 2 minutes proactive renewal buffer

/**
 * Shared, browser-wide auth lifecycle marker (localStorage). It never holds a credential —
 * only `{ status: "authenticated" | "logged_out", at, revokedAt? }` — and exists so that a
 * tab can tell, after an asynchronous operation (refresh), whether a logout or a fresh login
 * happened elsewhere in the meantime. Timestamps are this browser's wall clock, shared by all
 * tabs, so ordering between tabs is well defined.
 */
export const AUTH_STATE_STORAGE_KEY = "synexos_auth_state";

/** Window event dispatched when this tab's identity no longer matches the shared session. */
export const IDENTITY_CHANGED_EVENT = "synexos:identity-changed";

/** Window event dispatched when this tab observed a logout performed in another tab. */
export const EXTERNAL_LOGOUT_EVENT = "synexos:external-logout";

/** Error message used when a refresh is abandoned because the shared identity changed. */
export const IDENTITY_CHANGED_ERROR = "SESSION_IDENTITY_CHANGED";

const TOKEN_STORAGE_KEYS = ["token", "access_token"];
const AUTH_STORAGE_KEYS = new Set(["token", "access_token", "user", AUTH_STATE_STORAGE_KEY]);

let proactiveRefreshTimer = null;
let getMePromise = null;
let refreshPromise = null;
let authSessionEpoch = 0;

export const decodeJwt = (token) => {
  if (!token) return null;
  try {
    const base64Url = token.split('.')[1];
    if (!base64Url) return null;

    let base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const pad = base64.length % 4;
    if (pad) {
      base64 += '='.repeat(4 - pad);
    }

    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    logger.error("[Auth] Token decoding failed");
    return null;
  }
};

/**
 * Determine whether an error is a definitive authentication failure requiring session destruction.
 *
 * Rules:
 * - 403 Forbidden is NOT an authentication failure (it indicates lack of permission on a specific resource,
 *   solution gating, or a locked room). It MUST NEVER destroy the global user session.
 * - Only 401 Unauthorized from auth/refresh endpoints or explicit token revocation/deactivation messages
 *   count as definitive authentication failures.
 */
export const isDefinitiveAuthError = (err) => {
  if (!err) return false;
  const status = err.response?.status;
  const url = err.config?.url || "";
  const isAuthOrRefreshUrl =
    url.includes("/auth/login") ||
    url.includes("/enterprise/auth/refresh") ||
    url.includes("/auth/refresh");

  if (status === 401 && (isAuthOrRefreshUrl || !err.config)) {
    return true;
  }

  const msg = (
    err.message ||
    err.response?.data?.detail ||
    err.response?.data?.message ||
    ""
  ).toString().toLowerCase();

  if (
    msg.includes("invalid token") ||
    msg.includes("no refresh token") ||
    msg.includes("session expired") ||
    msg.includes("invalid refresh token") ||
    msg.includes("refresh token has been revoked") ||
    msg.includes("token has expired") ||
    msg.includes("user not found") ||
    msg.includes("account is inactive") ||
    msg.includes("account_deactivated")
  ) {
    return true;
  }

  return false;
};

export const getTokenRemainingSeconds = (token) => {
  if (!token) return 0;
  try {
    const payload = decodeJwt(token);
    if (!payload || typeof payload.exp !== 'number') return 0;
    const now = Math.floor(Date.now() / 1000);
    return Math.max(0, payload.exp - now);
  } catch {
    return 0;
  }
};

export const isTokenExpired = (token) => {
  if (!token) return true;
  try {
    const payload = decodeJwt(token);
    if (!payload) return true;
    if (typeof payload.exp === 'number') {
      const now = Math.floor(Date.now() / 1000);
      return payload.exp <= now;
    }
    return false;
  } catch {
    return true;
  }
};

export const isTokenExpiringSoon = (token, bufferSeconds = REFRESH_BUFFER_SECONDS) => {
  if (!token) return true;
  return getTokenRemainingSeconds(token) <= bufferSeconds;
};

// ─── Token identity ─────────────────────────────────────────────────────────────
// The identity a token represents is (user, organisation) as asserted by the JWT itself.
// It is decoded locally for comparison only — the backend remains the sole validator.

let _identityCacheToken = null;
let _identityCacheValue = null;

export const getTokenIdentity = (token) => {
  if (!token) return null;
  if (token === _identityCacheToken) return _identityCacheValue;
  const payload = decodeJwt(token);
  const uid = payload ? payload.user_id || payload.sub || null : null;
  const value = uid
    ? {
        uid: String(uid),
        org: payload.org_id ? String(payload.org_id) : null,
        exp: typeof payload.exp === "number" ? payload.exp : 0,
      }
    : null;
  _identityCacheToken = token;
  _identityCacheValue = value;
  return value;
};

export const isSameIdentity = (a, b) =>
  Boolean(a && b && a.uid === b.uid && (a.org || null) === (b.org || null));

// ─── Storage helpers ────────────────────────────────────────────────────────────

let _storageAvailable = null;
function storageAvailable() {
  if (_storageAvailable !== null) return _storageAvailable;
  try {
    if (typeof window === "undefined" || !window.localStorage) {
      _storageAvailable = false;
    } else {
      const probe = "__synexos_storage_probe__";
      window.localStorage.setItem(probe, "1");
      window.localStorage.removeItem(probe);
      _storageAvailable = true;
    }
  } catch {
    _storageAvailable = false;
  }
  return _storageAvailable;
}

function safeGet(key) {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

/** Returns false when the write failed (quota / disabled storage). */
function safeSet(key, value) {
  try {
    localStorage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
}

function safeRemove(key) {
  try {
    localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

function readStoredToken() {
  for (const key of TOKEN_STORAGE_KEYS) {
    const v = safeGet(key);
    if (v) return v;
  }
  return null;
}

export function readAuthState() {
  const raw = safeGet(AUTH_STATE_STORAGE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

function writeAuthState(patch) {
  const next = { ...(readAuthState() || {}), ...patch };
  safeSet(AUTH_STATE_STORAGE_KEY, JSON.stringify(next));
  return next;
}

/**
 * Clear tab-scoped (sessionStorage) main-app keys while preserving tab-scoped meeting
 * sessions, exactly as the original logout did.
 */
function clearTabScopedSession() {
  if (typeof sessionStorage === "undefined") return;
  try {
    const preserved = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const k = sessionStorage.key(i);
      if (
        k &&
        (k.startsWith('synexos_meeting_session_') ||
          k.startsWith('synex.guest.') ||
          k.startsWith(TAB_PRESERVED_SESSION_PREFIX))
      ) {
        preserved.push({ key: k, value: sessionStorage.getItem(k) });
      }
    }
    sessionStorage.clear();
    preserved.forEach(({ key, value }) => {
      try {
        sessionStorage.setItem(key, value);
      } catch {
        /* ignore */
      }
    });
  } catch {
    /* ignore */
  }
}

/**
 * sessionStorage keys with this prefix identify the browser tab itself (e.g. the Grid
 * recovery tab id) and must survive logout so recovery data stays attributable.
 */
export const TAB_PRESERVED_SESSION_PREFIX = "synexos_tab_";

const LOCK_ACQUISITION_TIMEOUT_MS = 8000;
const TAB_LOCK_OWNER_ID = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

/**
 * Cross-tab lock coordination helper.
 * Prefers navigator.locks when available (with an 8-second bounded acquisition timeout),
 * falling back to localStorage timestamp spinlock or direct execution.
 *
 * Web Locks are released by the browser automatically when the holding tab closes or
 * crashes. The fallback spinlock records `<timestamp>|<owner>`; a lock older than the
 * timeout is treated as abandoned (crashed tab), and a tab only removes a lock it owns.
 */
export async function runWithCrossTabLock(lockName, fn, timeoutMs = LOCK_ACQUISITION_TIMEOUT_MS) {
  if (typeof navigator !== 'undefined' && navigator.locks && typeof navigator.locks.request === 'function') {
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    let timer = null;
    if (controller) {
      timer = setTimeout(() => {
        try {
          controller.abort(new Error(`Web Lock acquisition timed out for "${lockName}" after ${timeoutMs}ms`));
        } catch {
          /* ignore */
        }
      }, timeoutMs);
    }

    try {
      const options = controller ? { signal: controller.signal } : {};
      return await navigator.locks.request(lockName, options, async () => {
        if (timer) {
          clearTimeout(timer);
          timer = null;
        }
        return await fn();
      });
    } catch (err) {
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
      // If lock acquisition was aborted due to timeout or lock contention:
      if (err.name === 'AbortError' || err.message?.includes('timed out') || err.message?.includes('aborted')) {
        logger.warn(`[AuthLock] Web Lock acquisition timed out for "${lockName}". Proceeding with direct refresh fallback...`);
        logAuthDiagnostic("authLock.acquisition_timeout_fallback", { lockName, timeoutMs });
        return await fn();
      }
      throw err;
    }
  }

  // Fallback: localStorage timestamp spinlock for environments without Web Locks API
  const LOCK_KEY = `lock_${lockName}`;
  const RETRY_INTERVAL_MS = 50;
  const start = Date.now();

  const readLock = () => {
    const raw = safeGet(LOCK_KEY);
    if (!raw) return null;
    const [ts, owner] = String(raw).split("|");
    return { ts: Number(ts), owner: owner || null };
  };

  while (Date.now() - start < timeoutMs) {
    const now = Date.now();
    const existing = readLock();
    if (!existing || !Number.isFinite(existing.ts) || now - existing.ts > timeoutMs) {
      safeSet(LOCK_KEY, `${now}|${TAB_LOCK_OWNER_ID}`);
      // Re-read: if another tab wrote in the same instant, the last writer wins and
      // the loser keeps spinning instead of both entering the critical section.
      const confirmed = readLock();
      if (confirmed && confirmed.owner === TAB_LOCK_OWNER_ID) {
        try {
          return await fn();
        } finally {
          const current = readLock();
          if (current && current.owner === TAB_LOCK_OWNER_ID) {
            safeRemove(LOCK_KEY);
          }
        }
      }
    }
    await new Promise((resolve) => setTimeout(resolve, RETRY_INTERVAL_MS));
  }

  logger.warn(`[AuthLock] Spinlock timed out for "${lockName}". Proceeding with direct refresh fallback...`);
  return fn();
}

let _accessToken = null;
let _user = null;
let _userRaw = null;
let _tokenWriteFailed = false; // last token write did not reach storage
let _userWriteFailed = false; // last user write did not reach storage
/** Non-null once this tab's identity diverged from the shared session: { from, to }. */
let _identityChange = null;

function dispatchWindowEvent(name, detail) {
  if (typeof window === "undefined" || typeof window.dispatchEvent !== "function") return;
  try {
    window.dispatchEvent(new CustomEvent(name, { detail }));
  } catch {
    /* ignore */
  }
}

export const authService = {
  /**
   * Current access token for this tab.
   *
   * localStorage is the browser-wide source of truth shared by every tab; the in-memory copy
   * is only a cache. Every read reconciles the two so that:
   *   - a token refreshed by another tab is adopted (no redundant refresh, no stale Bearer),
   *   - a logout performed by another tab invalidates this tab immediately,
   *   - a login as a DIFFERENT user/organisation in another tab never silently re-identifies
   *     this tab: it is frozen (no credentials) until the user reloads it.
   */
  getToken() {
    if (_identityChange) return null;
    if (typeof window === 'undefined' || !storageAvailable()) {
      return _accessToken;
    }

    const stored = readStoredToken();
    if (stored === _accessToken) return _accessToken;

    if (!stored) {
      // Storage holds no token while memory does: the session was ended in another tab
      // (logout, definitive refresh failure, or site data cleared). Only trust memory when
      // our own last storage write failed, i.e. storage never had the token to begin with.
      if (_accessToken && !_tokenWriteFailed) {
        this._applyExternalLogout("token_removed");
        return null;
      }
      return _accessToken;
    }

    if (!_accessToken) {
      if (!this.isTokenExpired(stored)) {
        _accessToken = stored;
        return _accessToken;
      }
      return null;
    }

    // Both present and different.
    const storedId = getTokenIdentity(stored);
    const memoryId = getTokenIdentity(_accessToken);
    if (!storedId) return _accessToken; // unreadable value in storage: never adopt it
    if (memoryId && !isSameIdentity(storedId, memoryId)) {
      this._applyIdentityChange(memoryId, storedId);
      return null;
    }
    // Same identity: adopt the stored token unless it is strictly older than ours.
    if (!memoryId || storedId.exp >= memoryId.exp || this.isTokenExpired(_accessToken)) {
      _accessToken = stored;
    }
    return _accessToken;
  },

  setToken(token) {
    if (_identityChange && token) {
      logAuthDiagnostic("authService.setToken_blocked_identity_changed");
      return;
    }
    _accessToken = token || null;
    if (typeof window !== 'undefined') {
      try {
        if (token) {
          localStorage.setItem('token', token);
          _tokenWriteFailed = false;
        } else {
          localStorage.removeItem('token');
          localStorage.removeItem('access_token');
        }
      } catch {
        _tokenWriteFailed = Boolean(token);
      }
    }
    if (_accessToken) {
      this.startProactiveRefresh();
    } else {
      this.stopProactiveRefresh();
    }
  },

  clearToken() {
    _accessToken = null;
    if (typeof window !== 'undefined') {
      try {
        localStorage.removeItem('token');
        localStorage.removeItem('access_token');
      } catch {
        /* ignore */
      }
    }
    this.stopProactiveRefresh();
  },

  getUser() {
    if (_identityChange) return null;
    if (typeof window !== 'undefined' && storageAvailable()) {
      const raw = safeGet('user');
      if (raw !== _userRaw) {
        if (raw === null) {
          // Removed by another tab (logout) unless our own write never reached storage.
          if (!_userWriteFailed) {
            _user = null;
            _userRaw = null;
          }
        } else {
          try {
            const parsed = JSON.parse(raw);
            _user = parsed && typeof parsed === "object" ? parsed : null;
            _userRaw = raw;
          } catch {
            _user = null;
            _userRaw = raw;
          }
        }
      }
    }
    // Snapshot: getOrgRole() → getToken() may reconcile and clear _user mid-expression.
    const user = _user;
    if (user) {
      const orgRole = this.getOrgRole();
      user.orgRole = orgRole || user.role_id || user.role;
    }
    return _identityChange ? null : _user;
  },

  setUser(user) {
    if (_identityChange && user) {
      // A tab frozen on a superseded identity must never write that identity back.
      logAuthDiagnostic("authService.setUser_blocked_identity_changed");
      return;
    }
    _user = user ? { ...user } : null;
    if (_user) {
      // Strip any accidental tokens from user object before local persistence
      delete _user.access_token;
      delete _user.refresh_token;
      delete _user.token;
      try {
        const raw = JSON.stringify(_user);
        localStorage.setItem('user', raw);
        _userRaw = raw;
        _userWriteFailed = false;
      } catch {
        _userWriteFailed = true;
      }
    } else {
      _userRaw = null;
      try {
        localStorage.removeItem('user');
      } catch {
        /* ignore */
      }
    }
  },

  isAuthenticated() {
    const token = this.getToken();
    return Boolean(token && !this.isTokenExpired(token));
  },

  hasRefreshToken() {
    // An active session is indicated by an access token or an initialized user in storage/memory.
    // After explicit logout returns false until a new login occurs.
    return Boolean(this.getToken() || this.getUser());
  },

  /** True when another tab signed in as a different user/organisation (see getToken). */
  isIdentityChanged() {
    // Reconcile first so callers get an up-to-date answer.
    this.getToken();
    return Boolean(_identityChange);
  },

  getIdentityChange() {
    return _identityChange;
  },

  /**
   * Invalidate this tab's in-memory session because another tab ended it. Never performs a
   * network call and never writes credentials; idempotent.
   */
  _applyExternalLogout(reason = "external") {
    const hadSession = Boolean(_accessToken || _user);
    authSessionEpoch++;
    getMePromise = null;
    refreshPromise = null;
    this.stopProactiveRefresh();
    _accessToken = null;
    _user = null;
    _userRaw = null;
    if (hadSession) {
      clearTabScopedSession();
      logAuthDiagnostic("authService.external_logout_applied", { reason });
      dispatchWindowEvent(EXTERNAL_LOGOUT_EVENT, { reason });
    }
  },

  _applyIdentityChange(from, to) {
    if (_identityChange) return;
    authSessionEpoch++;
    getMePromise = null;
    refreshPromise = null;
    this.stopProactiveRefresh();
    _accessToken = null;
    _user = null;
    _userRaw = null;
    _identityChange = {
      from: from ? { uid: from.uid, org: from.org } : null,
      to: to ? { uid: to.uid, org: to.org } : null,
      at: Date.now(),
    };
    logAuthDiagnostic("authService.identity_changed_in_other_tab", {
      sameUser: Boolean(from && to && from.uid === to.uid),
    });
    dispatchWindowEvent(IDENTITY_CHANGED_EVENT, _identityChange);
  },

  /**
   * Re-evaluate this tab against shared storage. Called on cross-tab auth events; the event
   * payload itself is never trusted — storage is.
   */
  syncFromStorage() {
    this.getToken();
    this.getUser();
  },

  setSession(authData) {
    if (!authData || !authData.access_token) {
      logAuthDiagnostic("authService.setSession_missing_token", { authDataPresent: Boolean(authData) });
      return null;
    }

    authSessionEpoch++;
    getMePromise = null;
    refreshPromise = null;
    this.stopProactiveRefresh();
    // An explicit login in THIS tab is the safe identity transition.
    _identityChange = null;

    this.setToken(authData.access_token);
    if (authData.user) {
      const userObj = { ...authData.user };
      if (authData.role && !userObj.org_role) {
        userObj.org_role = authData.role;
      }
      this.setUser(userObj);
    }
    if (authData.org_status) localStorage.setItem('org_status', authData.org_status);
    if (authData.org_name) localStorage.setItem('org_name', authData.org_name);
    try {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
    } catch {
      /* ignore */
    }
    writeAuthState({ status: "authenticated", at: Date.now(), revokedAt: null });
    broadcastAuthSync(AUTH_SYNC_EVENTS.AUTH_LOGIN);
    this.startProactiveRefresh();
    return authData;
  },


  async login(credentials) {
    const response = await api.post(`/api/auth/login`, credentials);
    if (response.data?.access_token) {
      this.setSession(response.data);
    }
    return response.data;
  },

  startProactiveRefresh() {
    this.stopProactiveRefresh();

    const token = this.getToken();
    if (!token) {
      return;
    }

    const remainingSeconds = getTokenRemainingSeconds(token);
    if (remainingSeconds <= 0) {
      logAuthDiagnostic("token_refresh_scheduled", { remaining_seconds: 0, delay_ms: 0, reason: "already_expired" });
      this.refreshToken().catch((err) => {
        logAuthDiagnostic("token_refresh_definitive_failure", { trigger: "proactive_expired", error: err?.message });
      });
      return;
    }

    // Schedule renewal at (remaining - REFRESH_BUFFER_SECONDS)
    const targetDelaySeconds = Math.max(0, remainingSeconds - REFRESH_BUFFER_SECONDS);
    const targetDelayMs = targetDelaySeconds * 1000;

    logAuthDiagnostic("token_refresh_scheduled", {
      remaining_seconds: remainingSeconds,
      buffer_seconds: REFRESH_BUFFER_SECONDS,
      delay_ms: targetDelayMs,
    });

    proactiveRefreshTimer = setTimeout(async () => {
      try {
        logAuthDiagnostic("token_refresh_started", {
          trigger: "proactive",
          remaining_seconds: getTokenRemainingSeconds(this.getToken())
        });
        await this.refreshToken();
        logAuthDiagnostic("token_refresh_succeeded", { trigger: "proactive" });
      } catch (err) {
        if (isDefinitiveAuthError(err)) {
          logAuthDiagnostic("token_refresh_definitive_failure", { trigger: "proactive", error: err?.message });
          this.logout();
          if (typeof window !== "undefined" && !window.location.pathname.includes("/auth/login")) {
            window.location.href = "/auth/login";
          }
        } else {
          logAuthDiagnostic("token_refresh_transient_failure", { trigger: "proactive", error: err?.message });
          // Transient failure (network/timeout): schedule a bounded retry in 10s or before token expires
          const rem = getTokenRemainingSeconds(this.getToken());
          if (rem > 0) {
            const retryDelayMs = Math.min(10000, Math.max(1000, (rem / 2) * 1000));
            proactiveRefreshTimer = setTimeout(() => {
              this.startProactiveRefresh();
            }, retryDelayMs);
          }
        }
      }
    }, targetDelayMs);
  },

  stopProactiveRefresh() {
    if (proactiveRefreshTimer) {
      clearTimeout(proactiveRefreshTimer);
      proactiveRefreshTimer = null;
    }
  },

  async refreshToken() {
    logAuthDiagnostic("authService.refreshToken_called", {
      hasLock: Boolean(refreshPromise),
      hasRefreshToken: this.hasRefreshToken(),
    });

    if (refreshPromise) {
      return refreshPromise;
    }

    refreshPromise = (async () => {
      try {
        return await runWithCrossTabLock('synex_auth_refresh_lock', async () => {
          if (this.isIdentityChanged()) {
            throw new Error(IDENTITY_CHANGED_ERROR);
          }
          // Never refresh a session that was logged out (in this tab or another) before we
          // got the lock — e.g. a 401 on the logout request itself, or a refresh queued
          // behind another tab's logout. The refresh cookie may still be valid server-side
          // until revocation completes; using it here would revive the session.
          const stateAtLock = readAuthState();
          if (stateAtLock && stateAtLock.status === "logged_out" && !readStoredToken()) {
            logAuthDiagnostic("authService.refreshToken_refused_logged_out");
            throw new Error("Session expired");
          }
          // getToken() reconciles with shared storage, so a token another tab refreshed
          // while we waited for the lock is seen here and no second refresh is sent.
          const currentAccessToken = this.getToken();
          if (currentAccessToken && !isTokenExpiringSoon(currentAccessToken)) {
            logAuthDiagnostic("token_refresh_skipped_valid", { reason: "other_tab_refreshed" });
            this.startProactiveRefresh();
            return currentAccessToken;
          }

          logAuthDiagnostic("authService.refreshToken_posting_to_backend");
          const callEpoch = authSessionEpoch;
          const startedAt = Date.now();
          const identityBefore = getTokenIdentity(readStoredToken() || currentAccessToken);
          const user = this.getUser();
          const currentOrgId = user?.org_id;
          const refreshPayload = currentOrgId ? { org_id: currentOrgId } : {};
          const response = await api.post('/api/enterprise/auth/refresh', refreshPayload);

          if (callEpoch !== authSessionEpoch) {
            logAuthDiagnostic("authService.refreshToken_dropped_stale_epoch");
            return null;
          }

          // Cross-tab ordering guard: a delayed refresh result must never revive a session
          // that another tab ended, nor overwrite a newer login, while it was in flight.
          const sharedState = readAuthState();
          if (sharedState && sharedState.status === "authenticated" && sharedState.at > startedAt) {
            logAuthDiagnostic("authService.refreshToken_dropped_newer_login");
            this.syncFromStorage();
            // Same identity re-login elsewhere: its token is adopted and is the answer.
            const adopted = this.getToken();
            if (adopted && !this.isTokenExpired(adopted)) return adopted;
            throw new Error(IDENTITY_CHANGED_ERROR);
          }
          if (sharedState && sharedState.status === "logged_out" && sharedState.at >= startedAt) {
            logAuthDiagnostic("authService.refreshToken_dropped_logout_during_refresh");
            // The response rotated the refresh cookie after the logout revoked the old one.
            // Revoke the replacement too so the server session cannot be resumed.
            api.post('/api/enterprise/auth/logout', {}).catch(() => {});
            this._applyExternalLogout("logout_during_refresh");
            throw new Error("Session expired");
          }

          if (response?.data?.access_token) {
            const responseIdentity = getTokenIdentity(response.data.access_token);
            const storedNow = readStoredToken();
            const storedIdentity = getTokenIdentity(storedNow);
            if (
              storedIdentity &&
              responseIdentity &&
              !isSameIdentity(storedIdentity, responseIdentity) &&
              !isSameIdentity(storedIdentity, identityBefore)
            ) {
              // Another tab switched user/organisation while we refreshed: never overwrite it.
              logAuthDiagnostic("authService.refreshToken_dropped_identity_switch");
              this.syncFromStorage();
              const adopted = this.getToken();
              if (adopted && !this.isTokenExpired(adopted)) return adopted;
              throw new Error(IDENTITY_CHANGED_ERROR);
            }

            this.setToken(response.data.access_token);
            if (response.data.user) {
              const prev = this.getUser() || {};
              this.setUser({ ...prev, ...response.data.user });
            }
            if (response.data.organization?.org_name || response.data.organization?.name) {
              localStorage.setItem('org_name', response.data.organization.org_name || response.data.organization.name);
            }
            if (response.data.organization?.status) {
              localStorage.setItem('org_status', response.data.organization.status);
            }
            try {
              localStorage.removeItem('access_token');
              localStorage.removeItem('refresh_token');
            } catch {
              /* ignore */
            }
            logAuthDiagnostic("authService.refreshToken_success");
            broadcastAuthSync(AUTH_SYNC_EVENTS.AUTH_TOKEN_UPDATED);
            return response.data.access_token;
          }
          throw new Error("Invalid token refresh response");
        });
      } catch (error) {
        logger.error("[Auth] Silent token refresh failed:", error?.message || "Refresh error");
        logAuthDiagnostic("authService.refreshToken_failed", {
          error: error?.message || String(error),
        });
        if (isDefinitiveAuthError(error)) {
          this.clearToken();
        }
        throw error;
      } finally {
        refreshPromise = null;
      }
    })();

    return refreshPromise;
  },

  isTokenExpired(token) {
    if (!token) return true;
    try {
      const payload = decodeJwt(token);
      if (!payload) return true;

      if (typeof payload.exp === 'number') {
        const now = Math.floor(Date.now() / 1000);
        return payload.exp <= now;
      }

      return false;
    } catch (error) {
      logger.error("[Auth] Token decoding failed");
      return true;
    }
  },

  isTokenExpiringSoon(token, bufferSeconds = REFRESH_BUFFER_SECONDS) {
    return isTokenExpiringSoon(token, bufferSeconds);
  },

  getTokenRemainingSeconds(token) {
    return getTokenRemainingSeconds(token);
  },

  /**
   * Log out of this browser session.
   *
   * Ordering (multi-tab safe):
   *   1. Local invalidation first and synchronously: memory, storage, the shared
   *      `logged_out` marker, then a broadcast. Every tab stops using credentials at once.
   *   2. Server revocation runs under the same cross-tab lock as token refresh, so it can
   *      never interleave with an in-flight refresh in another tab (which would otherwise
   *      rotate a fresh refresh cookie after the revocation). If a concurrent logout in
   *      another tab already revoked the session, the redundant request is skipped.
   *
   * The returned shape is unchanged: { success, serverRevoked, timedOut, error }.
   */
  async logout() {
    logAuthDiagnostic("authService.logout_invoked");
    authSessionEpoch++;
    getMePromise = null;
    refreshPromise = null;
    this.stopProactiveRefresh();

    // A tab frozen on a superseded identity must not end the NEW session that now owns the
    // shared storage and the browser's refresh cookie. It only drops its own memory.
    if (_identityChange) {
      _accessToken = null;
      _user = null;
      _userRaw = null;
      logAuthDiagnostic("authService.logout_frozen_tab_local_only");
      return { success: true, serverRevoked: false, timedOut: false, error: null };
    }

    // 1. Capture current access token before clearing local state
    const token = this.getToken() || _accessToken;
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const priorState = readAuthState();
    const alreadyLoggedOut =
      !token &&
      !_user &&
      !readStoredToken() &&
      !safeGet('user') &&
      priorState &&
      priorState.status === "logged_out";

    // 2. Local invalidation (preserving tab-scoped meeting sessions)
    const localClearedAt = Date.now();
    this.clearToken();
    _user = null;
    _userRaw = null;
    _identityChange = null;
    try {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('org_status');
      localStorage.removeItem('org_name');
      localStorage.removeItem(ACTIVITY_STORAGE_KEY);
      localStorage.removeItem('chat_fav_chats');
      localStorage.removeItem('chan_fav_channels');
      localStorage.removeItem('chan_recent_searches');
      localStorage.removeItem('calendar_events');
      localStorage.removeItem('calendar_calendars');
    } catch {
      /* ignore */
    }
    clearTabScopedSession();
    if (!alreadyLoggedOut) {
      writeAuthState({ status: "logged_out", at: localClearedAt });
    }
    broadcastAuthSync(AUTH_SYNC_EVENTS.AUTH_LOGOUT, {}, { local: true });

    if (alreadyLoggedOut) {
      logAuthDiagnostic("authService.logout_skipped_already_logged_out");
      return { success: true, serverRevoked: true, timedOut: false, error: null };
    }

    let serverRevoked = false;
    let timedOut = false;
    let serverError = null;

    // 3. Server revocation, serialised with refresh across tabs (bounded wait for the caller)
    const revocation = runWithCrossTabLock('synex_auth_refresh_lock', async () => {
      const state = readAuthState();
      if (state && state.revokedAt && state.revokedAt >= localClearedAt) {
        // Another tab's logout completed revocation after our local clear.
        return { status: "ok", deduplicated: true };
      }
      if (state && state.status === "authenticated" && state.at > localClearedAt) {
        // A new login happened after this logout; revoking now would end THAT session.
        return { status: "ok", superseded: true };
      }
      const res = await api.post('/api/enterprise/auth/logout', {}, { headers });
      const latest = readAuthState();
      if (!latest || latest.status === "logged_out") {
        writeAuthState({ revokedAt: Date.now() });
      }
      return { status: "ok", res };
    });

    try {
      const result = await Promise.race([
        revocation,
        new Promise((resolve) => setTimeout(() => resolve({ status: "timeout" }), 2000)),
      ]);

      if (result && result.status === "ok") {
        serverRevoked = true;
        logAuthDiagnostic(
          result.deduplicated
            ? "authService.logout_server_revocation_deduplicated"
            : "authService.logout_server_revocation_confirmed"
        );
      } else {
        timedOut = true;
        logAuthDiagnostic("authService.logout_server_timed_out");
        // The request keeps running in the background; just never surface its rejection.
        revocation.catch(() => {});
      }
    } catch (err) {
      serverError = err;
      logAuthDiagnostic("authService.logout_server_failed", { error: err?.message || String(err) });
    }

    return {
      success: true,
      serverRevoked,
      timedOut,
      error: serverError ? (serverError?.message || String(serverError)) : null,
    };
  },


  getOrgRole() {
    const token = this.getToken();
    if (!token) return null;
    try {
      const payload = decodeJwt(token);
      return payload?.role_id || payload?.role || null;
    } catch (error) {
      return null;
    }
  },

  async getMe() {
    if (getMePromise) {
      logAuthDiagnostic("get_me_coalesced");
      return getMePromise;
    }

    getMePromise = (async () => {
      try {
        const callEpoch = authSessionEpoch;
        const currentToken = this.getToken();
        if (currentToken && isTokenExpiringSoon(currentToken)) {
          logAuthDiagnostic("get_me_proactive_refresh_precheck");
          try {
            await this.refreshToken();
          } catch (refErr) {
            if (isDefinitiveAuthError(refErr)) {
              throw refErr;
            }
            if (this.isTokenExpired(currentToken)) {
              throw refErr;
            }
          }
        }

        const activeToken = this.getToken();
        const response = await api.get('/api/auth/me');

        // Prevent stale asynchronous responses from overwriting identity after logout or account switch
        if (callEpoch !== authSessionEpoch || !this.getToken() || this.getToken() !== activeToken) {
          logAuthDiagnostic("authService.getMe_dropped_stale_epoch");
          return null;
        }

        if (response.data) {
          this.setUser(response.data.user || response.data);

          const org = response.data.org || response.data.organization;
          if (org?.status) {
            localStorage.setItem('org_status', org.status);
          }
          if (org?.name || org?.org_name) {
            localStorage.setItem('org_name', org.name || org.org_name);
          }
        }
        return response.data;
      } catch (error) {
        logger.error("[Auth] Error fetching current user:", error?.message || "User fetch error");
        throw error;
      }
    })().finally(() => {
      getMePromise = null;
    });

    return getMePromise;
  }
};

// ─── Cross-tab reconciliation triggers ──────────────────────────────────────────
// Storage is authoritative; these only prompt this tab to re-read it promptly instead of
// waiting for the next getToken() call. Installed once per tab.
if (typeof window !== "undefined" && !window.__SYNEXOS_AUTH_SYNC_INSTALLED__) {
  window.__SYNEXOS_AUTH_SYNC_INSTALLED__ = true;
  try {
    subscribeAuthSync((event) => {
      if (
        event &&
        (event.type === AUTH_SYNC_EVENTS.AUTH_LOGOUT ||
          event.type === AUTH_SYNC_EVENTS.AUTH_LOGIN ||
          event.type === AUTH_SYNC_EVENTS.AUTH_TOKEN_UPDATED)
      ) {
        authService.syncFromStorage();
      }
    });
    window.addEventListener("storage", (e) => {
      if (e.key === null || AUTH_STORAGE_KEYS.has(e.key)) {
        authService.syncFromStorage();
      }
    });
  } catch {
    /* ignore */
  }
}

/** True when a storage event concerns authentication state (used to filter listeners). */
export const isAuthStorageKey = (key) => key === null || key === undefined || AUTH_STORAGE_KEYS.has(key);

export const getAuthHeaders = () => {
  const token = authService.getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const getToken = () => authService.getToken();
export const getAccessToken = () => authService.getToken();

export default authService;
